<template>
  <div class="container-fluid sale-detail">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando detalle de compra...</p>
    </div>

    <div v-else class="sale-detail-content">
      <!-- View header -->
      <div class="view-header my-4">
        <nav class="view-header__breadcrumb" aria-label="breadcrumb">
          <router-link :to="{ name: 'Compras' }">Compras</router-link>
          <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
          <template v-if="purchase.pos && purchase.number">
            <span class="view-header__breadcrumb-current">
              {{ padLeft(purchase.pos, 4) }}-{{ padLeft(purchase.number, 8) }}
            </span>
          </template>
          <span v-else class="view-header__breadcrumb-current">#{{ purchaseId }}</span>
        </nav>

        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Detalle de compra</h1>
            <p class="view-header__subtitle">Información detallada de la compra.</p>
          </div>
        </div>
      </div>

      <!-- Header card -->
      <div class="card invoice-header-card mb-4">
        <div class="invoice-header-main">
          <div class="invoice-type-block">
            <div class="invoice-type-icon">
              <i class="bi bi-file-earmark-text"></i>
            </div>
            <div class="invoice-type-info">
              <span class="invoice-type-label">Tipo de documento</span>
              <span class="invoice-type-value">
                Compra<template v-if="purchase.letter"> {{ purchase.letter }}</template>
              </span>
              <span v-if="purchase.letter && purchase.letter !== 'P'" class="invoice-type-badge">
                Original
              </span>
            </div>
          </div>

          <div class="invoice-client-block">
            <div class="invoice-client-title">
              <span class="client-title-icon">
                <i class="bi bi-truck"></i>
              </span>
              <span>Datos del proveedor</span>
            </div>

            <div class="invoice-client-grid">
              <div class="client-field">
                <span class="field-label">Nombre / Razón social</span>
                <span class="field-value">{{ purchase.provider_name || '—' }}</span>
              </div>

              <div class="client-field">
                <span class="field-label">
                  <i class="bi bi-calendar3 field-icon"></i>
                  Fecha de emisión
                </span>
                <span class="field-value">{{ createdDateFormatted || '—' }}</span>
              </div>

              <div v-if="purchase.number && purchase.pos" class="client-field">
                <span class="field-label">
                  <i class="bi bi-file-text field-icon"></i>
                  N° de comprobante
                </span>
                <span class="field-value">
                  {{ padLeft(purchase.pos, 4) }}-{{ padLeft(purchase.number, 8) }}
                </span>
              </div>

              <div v-if="purchase.provider_cuit" class="client-field">
                <span class="field-label">
                  <i class="bi bi-card-text field-icon"></i>
                  CUIT
                </span>
                <span class="field-value">{{ purchase.provider_cuit }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasSecondaryInfo" class="invoice-header-secondary">
          <div v-if="purchase.response && purchase.response['CAE']" class="secondary-item">
            <span class="secondary-label">CAE</span>
            <span class="secondary-value secondary-value--mono">{{ purchase.response['CAE'] }}</span>
            <span v-if="caeExpDateFormatted" class="secondary-sublabel">
              (vto. {{ caeExpDateFormatted }})
            </span>
          </div>
        </div>
      </div>

      <!-- Items + totals -->
      <div class="row">
        <!-- Items table -->
        <div class="col-md-8 mb-4 mb-md-0">
          <div class="card items-card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-box-seam"></i>
                </span>
                <h5 class="mb-0">Items</h5>
              </div>
              <div v-if="purchase.purchase_details && purchase.purchase_details.length > 0" class="items-summary">
                <span class="items-summary-item">
                  Total de ítems: <strong>{{ purchase.purchase_details.length }}</strong>
                </span>
                <span v-if="totalUnits > 0" class="items-summary-sep">|</span>
                <span v-if="totalUnits > 0" class="items-summary-item">
                  Total de unidades: <strong>{{ totalUnits }}</strong>
                </span>
              </div>
            </div>
            <div class="card-body p-4">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Item</th>
                      <th class="col-small text-center">Cantidad</th>
                      <th class="col-small text-right">Precio unit.</th>
                      <th class="col-small text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(value, idx) in purchase.purchase_details"
                      :key="idx"
                    >
                      <td>{{ value.description }}</td>
                      <td class="text-center">{{ value.quantity }}</td>
                      <td class="text-right">{{ formatoDinamicoMoneda(value.price) }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatoDinamicoMoneda(value.price * value.quantity) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Totals panel -->
        <div class="col-md-4">
          <div class="card totals-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-coin"></i>
                </span>
                <h5 class="mb-0">Totales de la compra</h5>
              </div>
            </div>
            <div class="card-body">
              <div class="total-row">
                <span class="label">Subtotal</span>
                <span class="value">{{ formatoDinamicoMoneda(purchase.subtotal) }}</span>
              </div>

              <div v-if="purchase.discount > 0" class="total-row">
                <span class="label">Descuento</span>
                <span class="value text-success">
                  − {{ formatoDinamicoMoneda(purchase.discount) }}
                </span>
              </div>

              <template v-if="purchase.letter === 'A' || purchase.letter === 'M'">
                <div class="total-row">
                  <span class="label">Neto gravado</span>
                  <span class="value">{{ formatoDinamicoMoneda(purchase.nettaxed) }}</span>
                </div>
                <div
                  v-for="(tax, key) in purchase.taxes"
                  :key="key"
                  class="total-row"
                >
                  <span class="label">IVA {{ key }}%</span>
                  <span class="value">{{ formatoDinamicoMoneda(tax) }}</span>
                </div>
              </template>

              <div class="totals-divider"></div>

              <div class="total-row total-final">
                <span class="label">Total</span>
                <span class="value">{{ formatoDinamicoMoneda(purchase.total) }}</span>
              </div>

              <div v-if="hasPayments" class="payments-section">
                <div class="payments-title">Forma de pago</div>

                <div v-if="fundMovementAmount > 0" class="payment-row">
                  <span class="label">Efectivo</span>
                  <span class="value">
                    {{ formatoDinamicoMoneda(fundMovementAmount) }}
                  </span>
                </div>

                <div
                  v-for="(tpcheck, idx) in tpchecksList"
                  :key="'t-' + idx"
                  class="payment-row"
                >
                  <span class="label">
                    Cheque
                    <small class="text-muted d-block">N° {{ tpcheck.number }}</small>
                  </span>
                  <span class="value">{{ formatoDinamicoMoneda(tpcheck.amount) }}</span>
                </div>

                <div v-if="totalCtaCte > 0" class="payment-row">
                  <span class="label">Cta. Cte.</span>
                  <span class="value">{{ formatoDinamicoMoneda(totalCtaCte) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="purchase.notes" class="card notes-card">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-sticky"></i>
                </span>
                <h6 class="mb-0">Nota del proveedor</h6>
              </div>
            </div>
            <div class="card-body">
              <p class="mb-0 note-text">{{ purchase.notes }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";

export default {
  props: ["purchaseId"],
  mixins: [formatMoneyMixin],
  data() {
    return {
      loading: true,
      purchase: {
        letter: null,
        pos: null,
        number: null,
        provider_id: null,
        subtotal: null,
        discount: null,
        taxes: null,
        total: null,
        notes: '',
        purchase_details: [],
        provider: {
          name: null,
        },
      },
    };
  },
  computed: {
    createdDateFormatted() {
      if (!this.purchase.created_at) return '';
      return new Date(this.purchase.created_at).toLocaleDateString('es-AR');
    },
    totalCtaCte() {
      return this.purchase.accountmovements != null
        ? this.purchase.accountmovements.amount
        : 0;
    },
    fundMovementAmount() {
      const fm = this.purchase.fundMovement;
      if (!fm) return 0;
      if (typeof fm === 'number') return fm;
      return fm.amount || 0;
    },
    tpchecksList() {
      return Array.isArray(this.purchase.tpchecks) ? this.purchase.tpchecks : [];
    },
    hasPayments() {
      return (
        this.fundMovementAmount > 0 ||
        this.tpchecksList.length > 0 ||
        this.totalCtaCte > 0
      );
    },
    caeExpDateFormatted() {
      const raw = this.purchase.response && this.purchase.response['CAEFchVto'];
      if (!raw) return '';
      const str = String(raw);
      if (/^\d{8}$/.test(str)) {
        return `${str.slice(6, 8)}/${str.slice(4, 6)}/${str.slice(0, 4)}`;
      }
      const d = new Date(str);
      return isNaN(d) ? str : d.toLocaleDateString('es-AR');
    },
    hasSecondaryInfo() {
      return !!(this.purchase.response && this.purchase.response['CAE']);
    },
    totalUnits() {
      if (!Array.isArray(this.purchase.purchase_details)) return 0;
      return this.purchase.purchase_details.reduce((sum, item) => {
        const q = Number(item.quantity) || 0;
        return sum + q;
      }, 0);
    },
  },
  mounted() {
    this.getPurchaseDetail(this.purchaseId);
  },
  methods: {
    getPurchaseDetail(purchaseId) {
      this.loading = true;
      this.$http
        .get('/api/purchases/detail/' + purchaseId)
        .then(response => {
          if (response.data.success) {
            this.purchase = response.data.data;
          }
        })
        .catch(error => {
          console.error('Error al cargar detalle de compra:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    padLeft(number, length) {
      return number.toString().padStart(length, '0');
    },
  },
};
</script>
